import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { settingsAPI, queryTypesAPI } from '../services/api';
import { notify } from '../hooks/useNotificationStore';

interface CustomQueryTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (queryTypeId: number) => void;
}

interface Engine {
  name: string;
  configured: boolean;
  default_model: string;
  available_models: string[];
}

interface JudgmentType {
  id: string;
  name: string;
  description: string;
}

interface SelectedEngine {
  engine_name: string;
  model_name: string;
  tier: 'primary' | 'secondary' | 'tertiary';
}

const TIER_OPTIONS = [
  { value: 'primary', label: 'Primary', weight: 2.0 },
  { value: 'secondary', label: 'Secondary', weight: 1.5 },
  { value: 'tertiary', label: 'Tertiary', weight: 1.0 },
];

const CLAUDE_MODELS = [
  'claude-sonnet-4-20250514',
  'claude-opus-4-20250514',
  'claude-3-5-sonnet-20241022',
  'claude-3-opus-20240229',
];

export default function CustomQueryTypeModal({
  isOpen,
  onClose,
  onCreated,
}: CustomQueryTypeModalProps) {
  const [name, setName] = useState('');
  const [engines, setEngines] = useState<Engine[]>([]);
  const [judgmentTypes, setJudgmentTypes] = useState<JudgmentType[]>([]);
  const [selectedEngines, setSelectedEngines] = useState<SelectedEngine[]>([]);
  const [judgeModel, setJudgeModel] = useState('claude-sonnet-4-20250514');
  const [judgmentType, setJudgmentType] = useState('default');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
      // Reset form
      setName('');
      setSelectedEngines([]);
      setJudgeModel('claude-sonnet-4-20250514');
      setJudgmentType('default');
    }
  }, [isOpen]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const [enginesRes, judgmentRes] = await Promise.all([
        settingsAPI.listEngines(),
        settingsAPI.listJudgmentTypes(),
      ]);
      // Only show configured engines
      const configuredEngines = enginesRes.data.engines.filter(
        (e: Engine) => e.configured
      );
      setEngines(configuredEngines);
      setJudgmentTypes(judgmentRes.data.judgment_types);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      notify.error('Failed to load engine configuration');
    } finally {
      setLoading(false);
    }
  };

  const addEngine = (engineName: string) => {
    if (selectedEngines.length >= 5) {
      notify.warning('Maximum 5 engines allowed');
      return;
    }
    if (selectedEngines.some((e) => e.engine_name === engineName)) {
      return; // Already selected
    }
    const engine = engines.find((e) => e.name === engineName);
    if (!engine) return;

    // Default tier based on position
    const tier =
      selectedEngines.length < 2 ? 'primary' : 'secondary';

    setSelectedEngines([
      ...selectedEngines,
      {
        engine_name: engineName,
        model_name: engine.default_model,
        tier: tier as 'primary' | 'secondary' | 'tertiary',
      },
    ]);
  };

  const removeEngine = (engineName: string) => {
    setSelectedEngines(
      selectedEngines.filter((e) => e.engine_name !== engineName)
    );
  };

  const updateEngine = (
    engineName: string,
    field: 'model_name' | 'tier',
    value: string
  ) => {
    setSelectedEngines(
      selectedEngines.map((e) =>
        e.engine_name === engineName ? { ...e, [field]: value } : e
      )
    );
  };

  const getAvailableEngines = () => {
    return engines.filter(
      (e) => !selectedEngines.some((se) => se.engine_name === e.name)
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      notify.error('Please enter a name for your query type');
      return;
    }
    if (selectedEngines.length < 2) {
      notify.error('Please select at least 2 engines');
      return;
    }

    setCreating(true);
    try {
      // Generate short description (max ~50 chars)
      const engineNames = selectedEngines.map((e) => e.engine_name);
      const shortDesc = engineNames.length <= 2
        ? `Uses ${engineNames.join(' + ')}`
        : `Uses ${engineNames.slice(0, 2).join(', ')} +${engineNames.length - 2} more`;

      const data = {
        name: name.trim(),
        description: shortDesc,
        judge_model: judgeModel,
        consensus_approach: judgmentType,
        engines: selectedEngines.map((e) => ({
          engine_name: e.engine_name,
          model_name: e.model_name,
          tier: e.tier,
          weight: TIER_OPTIONS.find((t) => t.value === e.tier)?.weight || 1.0,
        })),
      };

      const response = await queryTypesAPI.create(data);
      notify.success(`Created "${name}" query type`);
      onCreated(response.data.id);
      onClose();
    } catch (error: any) {
      console.error('Failed to create query type:', error);
      const message =
        error.response?.data?.detail || 'Failed to create query type';
      notify.error(message);
    } finally {
      setCreating(false);
    }
  };

  const isValid = name.trim() && selectedEngines.length >= 2;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-surface-800 border border-surface-700 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-surface-700">
                  <Dialog.Title className="text-lg font-semibold text-surface-100">
                    Create Custom Query Type
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg hover:bg-surface-700 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-surface-400" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  ) : (
                    <>
                      {/* Name Input */}
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g., Code Review, Travel Research"
                          className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500"
                        />
                      </div>

                      {/* Engine Selection */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-surface-300">
                            AI Engines{' '}
                            <span className="text-surface-500">
                              ({selectedEngines.length}/5)
                            </span>
                          </label>
                          {selectedEngines.length < 2 && (
                            <span className="text-xs text-amber-400">
                              Select at least 2 engines
                            </span>
                          )}
                        </div>

                        {/* Selected Engines */}
                        {selectedEngines.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {selectedEngines.map((selected) => {
                              const engine = engines.find(
                                (e) => e.name === selected.engine_name
                              );
                              return (
                                <div
                                  key={selected.engine_name}
                                  className="flex items-center gap-3 p-3 bg-surface-700 rounded-lg"
                                >
                                  <span className="font-medium text-surface-200 min-w-[100px]">
                                    {selected.engine_name}
                                  </span>

                                  {/* Model Selector */}
                                  <select
                                    value={selected.model_name}
                                    onChange={(e) =>
                                      updateEngine(
                                        selected.engine_name,
                                        'model_name',
                                        e.target.value
                                      )
                                    }
                                    className="flex-1 px-2 py-1 bg-surface-600 border border-surface-500 rounded text-sm text-surface-200 focus:outline-none focus:border-primary-500"
                                  >
                                    {engine?.available_models.map((model) => (
                                      <option key={model} value={model}>
                                        {model}
                                      </option>
                                    ))}
                                  </select>

                                  {/* Tier Selector */}
                                  <select
                                    value={selected.tier}
                                    onChange={(e) =>
                                      updateEngine(
                                        selected.engine_name,
                                        'tier',
                                        e.target.value
                                      )
                                    }
                                    className="px-2 py-1 bg-surface-600 border border-surface-500 rounded text-sm text-surface-200 focus:outline-none focus:border-primary-500"
                                  >
                                    {TIER_OPTIONS.map((tier) => (
                                      <option key={tier.value} value={tier.value}>
                                        {tier.label} ({tier.weight}x)
                                      </option>
                                    ))}
                                  </select>

                                  {/* Remove Button */}
                                  <button
                                    onClick={() =>
                                      removeEngine(selected.engine_name)
                                    }
                                    className="p-1 rounded hover:bg-surface-500 text-surface-400 hover:text-red-400 transition-colors"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Add Engine */}
                        {selectedEngines.length < 5 &&
                          getAvailableEngines().length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {getAvailableEngines().map((engine) => (
                                <button
                                  key={engine.name}
                                  onClick={() => addEngine(engine.name)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-surface-700 hover:bg-surface-600 border border-surface-600 rounded-lg text-sm text-surface-300 transition-colors"
                                >
                                  <PlusIcon className="w-4 h-4" />
                                  {engine.name}
                                </button>
                              ))}
                            </div>
                          )}

                        {engines.length === 0 && (
                          <p className="text-sm text-amber-400">
                            No engines configured. Please add API keys in
                            Settings.
                          </p>
                        )}
                      </div>

                      {/* Judge Configuration */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-2">
                            Judge Model
                          </label>
                          <select
                            value={judgeModel}
                            onChange={(e) => setJudgeModel(e.target.value)}
                            className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-surface-200 focus:outline-none focus:border-primary-500"
                          >
                            {CLAUDE_MODELS.map((model) => (
                              <option key={model} value={model}>
                                {model}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-2">
                            Judgment Type
                          </label>
                          <select
                            value={judgmentType}
                            onChange={(e) => setJudgmentType(e.target.value)}
                            className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-surface-200 focus:outline-none focus:border-primary-500"
                          >
                            {judgmentTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Judgment Type Description */}
                      {judgmentTypes.length > 0 && (
                        <p className="text-sm text-surface-400 -mt-2">
                          {
                            judgmentTypes.find((t) => t.id === judgmentType)
                              ?.description
                          }
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-surface-700">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-surface-300 hover:text-surface-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={!isValid || creating}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isValid && !creating
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-surface-600 text-surface-400 cursor-not-allowed'
                    }`}
                  >
                    {creating ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-surface-400 border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </span>
                    ) : (
                      'Create & Start'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
